import { useState } from 'react';

import { SEMANTIC_COLOR } from '@/shared/design-tokens/design-tokens';
import { ErrorFallback, type WidgetErrorProps } from '@/shared/ui/ErrorBoundary';

import { useDiskUsage } from '@/features/monitoring';
import type { DiskUsageData } from '@/features/monitoring/api';

import { CompactDualMetricCard } from '@/widgets/monitoring/CompactDualMetricCard';
import { MetricDetailModal } from '@/widgets/monitoring/MetricDetailModal';

import styles from './DiskMetric.module.css';

const DISK_READ_COLOR = SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.INDIGO;
const DISK_WRITE_COLOR = SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.MAGENTA;

type DiskMetricContentProps = {
  data?: DiskUsageData;
  isLoading?: boolean;
};

type DiskMetricViewProps = {
  data?: DiskUsageData;
  isLoading?: boolean;
};

const DiskMetricView = ({ data: diskData, isLoading: diskLoading = false }: DiskMetricViewProps) => {
  const [diskModalOpen, setDiskModalOpen] = useState(false);

  return (
    <div className={styles.container}>
      <CompactDualMetricCard
        title="Disk I/O"
        primaryLabel="읽기"
        primaryValue={diskData?.read.current ?? 0}
        primaryHistory={diskData?.read.history ?? []}
        primaryPodHistory={diskData?.read.podHistory ?? []}
        primaryColor={DISK_READ_COLOR}
        secondaryLabel="쓰기"
        secondaryValue={diskData?.write.current ?? 0}
        secondaryHistory={diskData?.write.history ?? []}
        secondaryPodHistory={diskData?.write.podHistory ?? []}
        secondaryColor={DISK_WRITE_COLOR}
        unit={diskData?.unit ?? 'KB/s'}
        isLoading={diskLoading}
        onClick={() => setDiskModalOpen(true)}
      />

      {diskData && (
        <MetricDetailModal
          open={diskModalOpen}
          onClose={() => setDiskModalOpen(false)}
          title="Disk I/O"
          primaryLabel="읽기"
          primaryValue={diskData.read.current}
          primaryHistory={diskData.read.history}
          primaryColor={DISK_READ_COLOR}
          secondaryLabel="쓰기"
          secondaryValue={diskData.write.current}
          secondaryHistory={diskData.write.history}
          secondaryColor={DISK_WRITE_COLOR}
          unit={diskData.unit}
        />
      )}
    </div>
  );
};

const ConnectedContent = () => {
  const { data, isLoading } = useDiskUsage();

  return <DiskMetricView data={data} isLoading={isLoading} />;
};

const Content = ({ data, isLoading }: DiskMetricContentProps) => {
  if (data) {
    return <DiskMetricView data={data} isLoading={isLoading} />;
  }

  return <ConnectedContent />;
};

const Error = ({ title, description, ...props }: WidgetErrorProps) => {
  return (
    <div className={styles.container}>
      <ErrorFallback title={title} description={description} className={styles.error} {...props} />
    </div>
  );
};

export const DiskMetric = { Content, Error };
